import { For } from "solid-js";
import { resolveStringAccessor } from "./MetadataFilterFields";
import { CHEAT_SHEET_STATE_OPTIONS, normalizeCheatSheetStateTokens } from "../../utils/metadataFilterTokens";

/**
 * Foiling-state (H0/H1/H2) multi-select chips for the Cheat Sheet page settings, shared by
 * the source (CheatSheetSettings) and fleet (RaceSettings) variants. Models PerfSettings'
 * state-chip UX (green when selected) but enforces the cheat-sheet contract: at least one
 * state is always selected — clicking the last selected chip off is a no-op, so the value
 * can never be empty (H0-only is the default).
 *
 * The value is a comma-joined string (same shape as the Year/Event filter strings); the
 * server matches each state with contains/LIKE so combined maneuver states (e.g. "H0-H1")
 * are allowed when they contain any selected state.
 */
interface CheatSheetStateFilterProps {
  /** Current comma-joined state string (accessor or plain string). */
  value?: string | (() => string);
  /** Called with the new comma-joined state string when the selection changes. */
  onChange?: (value: string) => void;
}

export default function CheatSheetStateFilter(props: CheatSheetStateFilterProps) {
  const selected = () => normalizeCheatSheetStateTokens(resolveStringAccessor(props.value));

  const isSelected = (state: string) =>
    selected().some((val) => val.trim().toLowerCase() === state.trim().toLowerCase());

  const toggle = (state: string) => {
    const current = selected();
    const has = isSelected(state);
    // Enforce force-at-least-H0: don't allow deselecting the last remaining state.
    if (has && current.length === 1) return;
    const next = has
      ? current.filter((s) => s.trim().toLowerCase() !== state.trim().toLowerCase())
      // Keep canonical H0→H1→H2 order regardless of click order.
      : CHEAT_SHEET_STATE_OPTIONS.filter((s) => current.includes(s) || s === state);
    props.onChange?.(next.join(","));
  };

  return (
    <div>
      <div class="text-sm font-medium mb-2" style="color: var(--color-text-primary);">
        Foiling State
      </div>
      <div class="flex flex-wrap gap-2">
        <For each={[...CHEAT_SHEET_STATE_OPTIONS]}>
          {(state) => (
            <span
              class={`px-3 py-1 rounded-full text-sm cursor-pointer ${
                isSelected(state) ? "bg-green-500 text-white" : "bg-gray-200 text-gray-700"
              }`}
              onClick={() => toggle(state)}
              onContextMenu={(e) => e.preventDefault()}
            >
              {state}
            </span>
          )}
        </For>
      </div>
    </div>
  );
}
